import React, { useEffect, useMemo, useState } from "react";
import { useCatalog } from "../../context/CatalogContext";

/**
 * Center-screen promo popup. Fully driven by hero config saved by the admin.
 *
 * Two modes:
 *   - image: shows an uploaded creative (with an optional CTA button on top)
 *   - text:  shows an editable headline + body (with an optional CTA button)
 *
 * Visibility:
 *   - Requires hero.bannerEnabled === true.
 *   - Shows once per browser (localStorage) so returning visitors are not
 *     nagged. Whenever the admin changes the banner content, the signature
 *     changes and the popup automatically re-appears for everyone.
 */
export default function PromoPopup() {
  const { hero } = useCatalog();
  const enabled = !!(hero && hero.bannerEnabled);

  const config = normaliseBannerConfig(hero);
  const signature = useSignature(config, enabled);
  const hasContent = bannerHasContent(config);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled || !hasContent) {
      setOpen(false);
      return;
    }
    try {
      const seen = localStorage.getItem("alankar_promo_seen");
      if (seen !== signature) {
        const t = setTimeout(() => setOpen(true), 500);
        return () => clearTimeout(t);
      }
    } catch (_) {
      setOpen(true);
    }
  }, [enabled, hasContent, signature]);

  const dismiss = () => {
    setOpen(false);
    try {
      localStorage.setItem("alankar_promo_seen", signature);
    } catch (_) {}
  };

  if (!enabled || !hasContent || !open) return null;
  return <PromoPopupSurface config={config} onDismiss={dismiss} />;
}

/**
 * Preview variant used inside the admin panel. Renders the popup exactly as
 * a first-time visitor would see it, using live form state — no
 * localStorage gating, no bannerEnabled check.
 */
export function PromoPopupPreview({ form, onClose }) {
  const config = normaliseBannerConfig(form || {});
  if (!bannerHasContent(config)) {
    return (
      <div
        className="promo-popup-backdrop"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        data-testid="promo-popup-preview-empty"
      >
        <div
          className="promo-popup promo-popup--text"
          onClick={(e) => e.stopPropagation()}
          style={{ background: "#1c110a", color: "#F7F0E4" }}
        >
          <button
            type="button"
            className="promo-popup__close"
            onClick={onClose}
            aria-label="Close preview"
          >
            <span aria-hidden="true">×</span>
          </button>
          <div className="promo-popup__text">
            <h3>Nothing to preview yet</h3>
            <p>
              {config.mode === "image"
                ? "Upload an image above and it will appear here."
                : "Add a headline or description above and it will appear here."}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return <PromoPopupSurface config={config} onDismiss={onClose} isPreview />;
}

// ------------------------------------------------------------------
// Internal helpers
// ------------------------------------------------------------------

function normaliseBannerConfig(src) {
  return {
    mode: src?.bannerMode || "image",
    image: src?.bannerImage || "",
    heading: src?.bannerHeading || "",
    body: src?.bannerBody || "",
    bg: src?.bannerBgColor || "#1c110a",
    fg: src?.bannerTextColor || "#F7F0E4",
    buttonEnabled: src?.bannerButtonEnabled !== false,
    buttonLabel: (src?.bannerButtonLabel || "").trim(),
    buttonLink: (src?.bannerButtonLink || "").trim(),
    legacyImageLink: src?.bannerLink || "",
  };
}

function bannerHasContent(config) {
  if (config.mode === "image") return !!config.image;
  return !!(config.heading || config.body);
}

function useSignature(config, enabled) {
  return useMemo(
    () =>
      [
        enabled ? "1" : "0",
        config.mode,
        config.image,
        config.heading,
        config.body,
        config.bg,
        config.fg,
        config.buttonEnabled ? "b1" : "b0",
        config.buttonLabel,
        config.buttonLink,
        config.legacyImageLink,
      ].join("|"),
    [enabled, config]
  );
}

function PromoPopupSurface({ config, onDismiss, isPreview = false }) {
  const imageMode = config.mode === "image" && config.image;
  const button =
    config.buttonEnabled && config.buttonLabel ? (
      <a
        href={config.buttonLink || "#"}
        target="_top"
        rel="noopener noreferrer"
        className="promo-popup__cta"
        onClick={(e) => {
          if (isPreview) {
            e.preventDefault();
          }
          onDismiss();
        }}
        data-testid="promo-popup-cta"
      >
        {config.buttonLabel}
      </a>
    ) : null;

  const ImageEl = (
    <img
      src={config.image}
      alt={config.heading || "Promotion"}
      className="promo-popup__image"
      data-testid="promo-popup-image"
    />
  );

  return (
    <div
      className="promo-popup-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Promotional banner"
      onClick={onDismiss}
      data-testid={isPreview ? "promo-popup-preview" : "promo-popup-backdrop"}
    >
      <div
        className={`promo-popup ${imageMode ? "promo-popup--image" : "promo-popup--text"}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="promo-popup"
        style={imageMode ? undefined : { background: config.bg, color: config.fg }}
      >
        <button
          type="button"
          className="promo-popup__close"
          onClick={onDismiss}
          aria-label="Close promotional banner"
          data-testid="promo-popup-close"
        >
          <span aria-hidden="true">×</span>
        </button>

        {imageMode ? (
          <>
            {config.legacyImageLink && !button ? (
              <a
                href={config.legacyImageLink}
                target="_top"
                rel="noopener noreferrer"
                className="promo-popup__link"
                onClick={(e) => {
                  if (isPreview) e.preventDefault();
                  onDismiss();
                }}
                data-testid="promo-popup-link"
              >
                {ImageEl}
              </a>
            ) : (
              ImageEl
            )}
            {button ? <div className="promo-popup__cta-wrap">{button}</div> : null}
          </>
        ) : (
          <div className="promo-popup__text">
            {config.heading ? (
              <h3 data-testid="promo-popup-heading">{config.heading}</h3>
            ) : null}
            {config.body ? (
              <p data-testid="promo-popup-body">{config.body}</p>
            ) : null}
            {button}
          </div>
        )}

        {isPreview ? (
          <div className="promo-popup__preview-badge" aria-hidden="true">
            Preview
          </div>
        ) : null}
      </div>
    </div>
  );
}
